'use client';
import useUser from '@haaretz/s-atoms/user';
import useCheckExistUser from '@haaretz/s-auth-hooks/use-check-exist-user';
import useRegistration from '@haaretz/s-auth-hooks/use-registration';
import useUserAlertsRegistration from '@haaretz/s-client-data-hooks/alerts/useUserAlertsRegistration';
import { siteId, site, siteAlias } from '@haaretz/s-consts';
import useBi from '@haaretz/s-use-bi';
import emailValidator from '@haaretz/s-validators/emailValidator';
import React from 'react';
const successMessages = 'The email address you have provided is already registered';
export default function useNewsletterRegistration({ tagNumber, resourceType, newsletterType, title, onRegistrationComplete, onError }) {
    const [errorMsg, setErrorMsg] = React.useState(null);
    const [btnState, setBtnState] = React.useState('auto');
    const newsletterSignup = useUserAlertsRegistration();
    const biAction = useBi();
    const user = useUser('cookieValue');
    const registerToNewsletter = React.useCallback(async ({ email })=>{
        setBtnState('busy');
        const data = await newsletterSignup.mutateAsync({
            email,
            site,
            resourceId: tagNumber || '',
            resourceType
        });
        const { status } = data;
        const emailProps = {
            isGmail: user.userMail?.endsWith('@gmail.com') || false,
            email: user.userMail || ''
        };
        if (status === 'success' || status === 'pending') {
            biAction({
                action_id: 9,
                newsletter_email: user.userMail || '',
                newsletter_segment_id: tagNumber || '',
                newsletter_segment_name: title,
                feature_type: 'Content',
                feature: `${newsletterType === 'personal' ? 'Personal' : 'Regular'}-Newsletter`,
                personal_feature: newsletterType === 'personal'
            });
            onRegistrationComplete?.(status, emailProps);
        }
        setBtnState('auto');
        return status;
    }, [
        newsletterSignup,
        user.userMail,
        tagNumber,
        resourceType,
        biAction,
        title,
        newsletterType,
        onRegistrationComplete
    ]);
    const newSiteRegistration = useRegistration({
        restEntryType: 'NEWSLETTER_MINI_REG'
    });
    const handleSubmit = React.useCallback(async ({ email })=>{
        const userEmail = email || user.userMail || '';
        const isValid = emailValidator(userEmail);
        if (!isValid.valid) {
            setErrorMsg(isValid.text);
            setBtnState('disabled');
            onError?.(isValid.text);
            return false;
        }
        if (user.isLoggedIn) {
            registerToNewsletter({
                email: userEmail
            });
        } else {
            const existingUser = await useCheckExistUser.fetcher({
                email: email || '',
                site: siteAlias
            })();
            const isExistingUser = !!existingUser.user;
            if (isExistingUser) {
                registerToNewsletter({
                    email
                });
            } else {
                const isSuccess = await newSiteRegistration.mutateAsync({
                    userName: user.userMail || email || '',
                    anonymousId: user.anonymousId,
                    newsso: 'true',
                    site: siteId.toString(),
                    termsChk: 'on',
                    restEntryType: 'NEWSLETTER_MINI_REG',
                    fromlogin: 'true',
                    layer: 'login'
                }).then((data)=>{
                    if (data.success === false) {
                        setErrorMsg(data.messages?.[0] || '');
                        setBtnState('disabled');
                        return false;
                    }
                    if (data.code === 403 && data.messages?.includes(successMessages) || data.code === 200 && data.success) registerToNewsletter({
                        email
                    });
                    return true;
                }).catch((error)=>{
                    setErrorMsg(error.message);
                    setBtnState('disabled');
                    onError?.(error.message);
                    return false;
                });
                if (!isSuccess) return false;
            }
        }
        setBtnState('busy');
        biAction({
            action_id: 60,
            newsletter_email: user.userMail || '',
            newsletter_segment_id: tagNumber || '',
            newsletter_segment_name: title,
            feature_type: 'Content',
            feature: newsletterType === 'personal' ? 'Personal-Newsletter' : 'Regular-Newsletter',
            personal_feature: newsletterType === 'personal'
        });
        return true;
    }, [
        user.userMail,
        user.isLoggedIn,
        user.anonymousId,
        biAction,
        tagNumber,
        title,
        newsletterType,
        onError,
        registerToNewsletter,
        newSiteRegistration
    ]);
    const clearError = React.useCallback(()=>{
        if (errorMsg) setErrorMsg(null);
        if (btnState === 'disabled') setBtnState('auto');
    }, [
        btnState,
        errorMsg
    ]);
    return {
        errorMsg,
        btnState,
        handleSubmit,
        clearError
    };
}

//# sourceMappingURL=useNewsletterRegistration.js.map